import { EmptyArray } from "../domain/empty/model";
import { ContableAcountsRepositoryImplementation } from '../infrastructure/contable-acounts-repository-implementation';

import { Filter } from '@/domain/pagination/models';

import { cacheManager } from '../infrastructure/cache-manager';

const repo = new ContableAcountsRepositoryImplementation();

const CACHE_PREFIX = 'contable_accounts';

async function getContableAcounts(page: number, limit: number, filters: Filter[]): Promise<any[]> {
    const response = await repo.getContableAcounts(page, limit, filters).toPromise();
    const data = response.data;
    return data;
}

let activePromise: Promise<any[]> | null = null;

async function getAllSubContableAcounts(uid: string, cid: string, forceReload = false): Promise<any[]> {
    const cacheKey = `${CACHE_PREFIX}-sub-all`;

    if (forceReload) {
        cacheManager.removeItem(cacheKey);
        activePromise = null;
    } else {
        const cached = cacheManager.getItem<any[]>(cacheKey);
        if (cached) return cached;
        if (activePromise) return activePromise;
    }

    activePromise = (async () => {
        try {
            const response = await repo.getAllSubContableAcounts(uid, cid).toPromise();
            if (!response || !response.data) return EmptyArray;
            const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
            if (data.length === 0) return EmptyArray;
            cacheManager.setItem(cacheKey, data);
            return data;
        } catch (error) {
            activePromise = null; // Reset on failure
            throw error;
        } finally {
            activePromise = null; // Clear active promise when finished
        }
    })();

    return activePromise;
}


async function getSubContableAcounts(uid: string, cid: string | null, id: number): Promise<any> {
    const cacheKey = `${CACHE_PREFIX}-sub-detail-${id}`;
    const cachedAcounts = cacheManager.getItem(cacheKey);
    if (cachedAcounts) {
        console.log(`Returning sub contable account with ID ${id} for ${uid}/${cid} from cache`);
        return cachedAcounts;
    }

    const response = await repo.getSubContableAcounts(uid, cid, id).toPromise();
    const data = response.data;
    if (data.length === 0) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
}

async function createAuxAccount(uid: string, cid: string | null, id: number, data: any): Promise<any> {
    const response = await repo.createAuxAcounts(uid, cid, id, data).toPromise();
    // Invalidate everything related to contable accounts
    cacheManager.invalidate(CACHE_PREFIX);
    await getAllSubContableAcounts(uid, cid || ''); // Re-fetch to update cache
    return response.data;
}

async function updateSubContableAcounts(account: string, id: number, data: any): Promise<any> {
    const response = await repo.updateSubContableAcounts(account, id, data).toPromise();
    // Update all relevant caches after a modification
    cacheManager.invalidate(CACHE_PREFIX);
    await getAllSubContableAcounts(account, ''); // Re-fetch to update cache
    return response.data;
}

const clearContableAccountsCache = () => {
    cacheManager.invalidate(CACHE_PREFIX);
};

export {
    getContableAcounts,
    getSubContableAcounts,
    createAuxAccount as setSubContableAcounts,
    getAllSubContableAcounts,
    updateSubContableAcounts,
    clearContableAccountsCache
};
